import { useEffect, useRef, useState, type JSX } from 'react';
import { CollapsibleBottomSheet } from '@/components/layout';
import {
  addProductToList,
  getUserListsPreview,
  removeProductFromList,
} from '@/services/slaydAPI';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useToastStore from '@/stores/ToastStore';
import styles from './CreateNewList.module.css';
import type { CreateNewListProps } from './CreateNewList.types';

const CreateNewList = ({
  productId,
  productImage,
}: CreateNewListProps): JSX.Element => {
  const {
    setSelectedSlaylist,
    closeSlaylistSelector,
    setSlaylists,
    setProductSlaylist,
    getSelectedSlaylist,
    getSlaylistIdbyName,
  } = useSlaylistSelectorStore();
  const [listName, setListName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { showToast } = useToastStore();

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 300); // A small delay ensures the sheet is animated in

    return () => clearTimeout(timer);
  }, []);

  const createSlaylist = async (): Promise<void> => {
    if (listName.trim()) {
      try {
        // Get current slaylist info for potential removal
        const currentSlaylistName = getSelectedSlaylist();
        const currentSlaylistId = getSlaylistIdbyName(currentSlaylistName);

        // Store previous state for potential rollback
        const previousSlaylistName = currentSlaylistName;
        const previousSlaylistId = currentSlaylistId;

        // Optimistically update UI immediately
        setSelectedSlaylist(listName);
        closeSlaylistSelector();

        try {
          // Remove from old list if it exists and is not 'Saved'
          if (
            currentSlaylistId &&
            currentSlaylistName &&
            currentSlaylistName !== 'Saved'
          ) {
            await removeProductFromList(currentSlaylistId, Number(productId));
          }

          // Add to new list
          await addProductToList(Number(productId), listName);

          // Update slaylists and get the new list info
          const freshSlaylists = await getUserListsPreview();
          setSlaylists(freshSlaylists || []);
          const newSlaylist = freshSlaylists?.find(
            (list) => list.name === listName
          );
          if (!newSlaylist?.id) {
            throw new Error('Could not find the new list after fetching.');
          }
          const newListId = newSlaylist.id;

          // Update product override
          setProductSlaylist(Number(productId), listName, newListId);

          showToast(`Added to '${listName}'`, {
            text: 'CHANGE',
            action: {
              type: 'change',
              productId: Number(productId),
              productImage: productImage || undefined,
            },
          });
        } catch (error) {
          console.error('Failed to create slaylist:', error);

          // Rollback optimistic updates
          if (previousSlaylistName) {
            setSelectedSlaylist(previousSlaylistName);
            setProductSlaylist(
              Number(productId),
              previousSlaylistName,
              previousSlaylistId || -1
            );
          } else {
            setSelectedSlaylist('');
            setProductSlaylist(Number(productId), '', -1);
          }

          showToast('Failed to create slaylist. Changes reverted.');
        }
      } catch (error) {
        console.error('Failed to create slaylist:', error);
        showToast('Failed to create slaylist');
      }
    }
  };

  return (
    <CollapsibleBottomSheet
      isExpanded={true}
      className={styles.selectorWrapper}
      maxHeight={234}
      onExpandedChange={(expanded) => {
        if (!expanded) {
          closeSlaylistSelector();
        }
      }}
      onCollapse={closeSlaylistSelector}
    >
      <div className={styles.content}>
        <div className={styles.sectionTitle}>Give name to this list</div>

        <div className={styles.inputContainer}>
          <input
            ref={inputRef}
            type="text"
            placeholder="For ex. 'office outfits', 'clubbing skirts'"
            value={listName}
            className={styles.textInput}
            onChange={(e) => setListName(e.target.value)}
          />
        </div>

        <button
          className={styles.createButton}
          disabled={!listName.trim()}
          onClick={createSlaylist}
        >
          Create
        </button>
      </div>
    </CollapsibleBottomSheet>
  );
};

export default CreateNewList;
